import { type DocumentNode, gql } from '@apollo/client';

/**
 * Variables for allViews query
 */
export interface AllViewsVariables {
  viewName: string;
  componentName?: string;
}

/**
 * Field structure within view field set
 */
export interface ViewSetField {
  id: string;
  name: string;
  slug: string;
  isEditable: boolean;
  fieldType: string;
  choices: string | null;
  scope: string | null;
  dateDisplayStyle?: string;
}

/**
 * View field set node structure
 */
export interface ViewFieldSetNode {
  id: string;
  fieldLabel: string;
  sequence: number;
  field: ViewSetField;
}

/**
 * View field set edge structure
 */
export interface ViewFieldSetEdge {
  node: ViewFieldSetNode;
}

/**
 * View field set connection structure
 */
export interface ViewFieldSetConnection {
  __typename: string;
  edges: ViewFieldSetEdge[];
}

/**
 * View node structure
 */
export interface ViewNode {
  id: string;
  viewName: string;
  componentName: string;
  viewfieldSet: ViewFieldSetConnection;
}

/**
 * View edge structure
 */
export interface ViewEdge {
  node: ViewNode;
}

/**
 * All views connection structure
 */
export interface AllViewsConnection {
  __typename: string;
  edges: ViewEdge[];
}

/**
 * Response structure for allViews query
 */
export interface AllViewsResponse {
  allViews: AllViewsConnection;
}

/**
 * Simple view node for minimal query
 */
export interface SimpleViewNode {
  componentName: string;
}

/**
 * Simple view edge
 */
export interface SimpleViewEdge {
  node: SimpleViewNode;
}

/**
 * Simple all views connection
 */
export interface SimpleAllViewsConnection {
  edges: SimpleViewEdge[];
}

/**
 * Response for simple allViews query
 */
export interface AllViewSimpleResponse {
  allViews: SimpleAllViewsConnection;
}

/**
 * Minimal field structure
 */
export interface MinimalViewSetField {
  slug: string;
  fieldType: string;
  scope: string | null;
}

/**
 * Minimal view field set node
 */
export interface MinimalViewFieldSetNode {
  fieldLabel: string;
  sequence: number;
  field: MinimalViewSetField;
}

/**
 * Minimal view field set edge
 */
export interface MinimalViewFieldSetEdge {
  node: MinimalViewFieldSetNode;
}

/**
 * Minimal view field set connection
 */
export interface MinimalViewFieldSetConnection {
  edges: MinimalViewFieldSetEdge[];
}

/**
 * Minimal view node
 */
export interface MinimalViewNode {
  viewfieldSet: MinimalViewFieldSetConnection;
}

/**
 * Minimal view edge
 */
export interface MinimalViewEdge {
  node: MinimalViewNode;
}

/**
 * Minimal all views connection
 */
export interface MinimalAllViewsConnection {
  __typename: string;
  edges: MinimalViewEdge[];
}

/**
 * Response for minimal allViews query
 */
export interface AllViewsMinResponse {
  allViews: MinimalAllViewsConnection;
}

const allViews: DocumentNode = gql`
  query allViews($viewName: String!, $componentName: String) {
    allViews(viewName: $viewName, componentName: $componentName) {
      __typename
      edges {
        node {
          id
          viewName
          componentName
          viewfieldSet {
            __typename
            edges {
              node {
                id
                fieldLabel
                sequence
                field {
                  id
                  name
                  slug
                  isEditable
                  fieldType
                  choices
                  scope
                  dateDisplayStyle
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const allViewSimple: DocumentNode = gql`
  query allViews($viewName: String!, $componentName: String) {
    allViews(viewName: $viewName, componentName: $componentName) {
      edges {
        node {
          componentName
        }
      }
    }
  }
`;

export const allViewsMin: DocumentNode = gql`
  query allViews($viewName: String!, $componentName: String) {
    allViews(viewName: $viewName, componentName: $componentName) {
      __typename
      edges {
        node {
          viewfieldSet {
            edges {
              node {
                fieldLabel
                sequence
                field {
                  slug
                  fieldType
                  scope
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default allViews;
